import { useState, useEffect } from 'react'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import * as firebaseui from 'firebaseui'
import { GoogleAuthProvider } from 'firebase/auth'
import { useNavigate } from 'react-router-dom'

/**
 * Hook personalizado para gestionar la autenticación con Firebase y la configuración de Firebase UI.
 *
 * Este hook observa el estado de autenticación del usuario, configura la UI de Firebase
 * para el login (usando el flujo en modo popup) y redirige al usuario a `/pedidos` si ya está autenticado.
 * Además, provee un indicador de si el usuario se encuentra autenticado.
 *
 * La complejidad de las operaciones que maneja este hook es aproximadamente O(1),
 * ya que cada evento de autenticación se procesa de forma independiente y en tiempo constante.
 *
 * @returns {Object} Un objeto con los siguientes atributos:
 *
 * @property {firebaseui.auth.AuthUI | null} ui - Instancia de la UI de Firebase Authentication. Se encarga de renderizar la interfaz de autenticación en el elemento con ID `#firebaseui-auth-container`.
 * @property {string} errorText - Mensaje de error en caso de fallo en la autenticación. Es una cadena vacía si no hay errores.
 * @property {boolean} loading - Indica si el estado de autenticación aún se está determinando.
 *   - `true`: el estado del usuario aún se está verificando.
 *   - `false`: se ha determinado que no hay usuario autenticado.
 * @property {boolean} isAuthenticated - Indica si el usuario está autenticado.
 *   - `true`: el usuario se encuentra autenticado.
 *   - `false`: el usuario no está autenticado.
 *
 * @example
 * // Uso en un componente React
 * const { ui, errorText, loading, isAuthenticated } = useFirebaseAuth();
 *
 * return (
 *   <div>
 *     {loading && <p>Cargando...</p>}
 *     {!loading && !isAuthenticated && (
 *       <>
 *         <h2>Iniciar Sesión</h2>
 *         <div id="firebaseui-auth-container"></div>
 *       </>
 *     )}
 *     {errorText && <p style={{ color: 'red' }}>{errorText}</p>}
 *   </div>
 * );
 *
 * @see {@link https://github.com/firebase/firebaseui-web} Documentación oficial de FirebaseUI.
 */
export const useFirebaseAuth = () => {
  /**
   * Instancia de FirebaseUI utilizada para manejar la UI de autenticación.
   * @type {[firebaseui.auth.AuthUI | null, function]}
   */
  const [ui, setUi] = useState(null)

  /**
   * Mensaje de error si la autenticación falla.
   * @type {[string, function]}
   */
  const [errorText, setErrorText] = useState('')

  /**
   * Indicador de carga para determinar si la autenticación se encuentra en proceso.
   * @type {[boolean, function]}
   */
  const [loading, setLoading] = useState(true)

  /**
   * Indicador que determina si el usuario se encuentra autenticado.
   * @type {[boolean, function]}
   */
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  /**
   * Instancia de autenticación de Firebase.
   * @type {import('firebase/auth').Auth}
   */
  const auth = getAuth()

  /**
   * Función de navegación de React Router para redirigir al usuario.
   * @type {import('react-router-dom').NavigateFunction}
   */
  const navigate = useNavigate()

  useEffect(() => {
    /**
     * Observa el estado de autenticación del usuario.
     * - Si el usuario está autenticado, establece `isAuthenticated` en true y redirige a `/pedidos`.
     * - Si no hay usuario, establece `loading` en false.
     *
     * @param {import('firebase/auth').Auth} auth - Instancia de autenticación de Firebase.
     * @param {function} user - Función que recibe el estado de autenticación del usuario.
     * @returns {function} Función para desregistrar el observador al desmontar el componente.
     */
    const unregisterAuthObserver = onAuthStateChanged(auth, user => {
      if (user) {
        setIsAuthenticated(true)
        navigate('/pedidos')
      } else {
        setIsAuthenticated(false)
        setLoading(false)
      }
    })

    /**
     * Configuración de FirebaseUI.
     * - `signInSuccessUrl`: URL de redirección tras el login exitoso.
     * - `signInFlow`: Modo de login (popup).
     * - `signInOptions`: Proveedores de autenticación (Google, restringido a un dominio específico).
     * - `credentialHelper`: Asistente de credenciales de Google YOLO.
     * - `callbacks`: Funciones para manejar el éxito o fallo del login y cuando la UI está lista.
     *
     * @type {firebaseui.auth.Config}
     */
    const uiConfig = {
      signInSuccessUrl: '/pedidos',
      signInFlow: 'popup',
      signInOptions: [
        {
          provider: GoogleAuthProvider.PROVIDER_ID,
          authMethod: 'https://accounts.google.com',
          customParameters: {
            hd: 'kitchencenter.cl' // Limita el acceso a este dominio
          }
        }
      ],
      credentialHelper: firebaseui.auth.CredentialHelper.GOOGLE_YOLO,
      callbacks: {
        /**
         * Callback que se ejecuta cuando el inicio de sesión es exitoso.
         *
         * @param {Object} _authResult - Resultado de la autenticación.
         * @param {string} _redirectUrl - URL a la que se redirige tras la autenticación.
         * @returns {boolean} Retorna `true` para redirigir a `signInSuccessUrl`.
         */
        signInSuccessWithAuthResult: (_authResult, _redirectUrl) => {
          setErrorText('')
          return true
        },
        /**
         * Callback que se ejecuta cuando ocurre un error en la autenticación.
         *
         * @param {Error} _error - Error ocurrido durante la autenticación.
         */
        signInFailure: _error => {
          setErrorText(
            'Hubo un error durante la autenticación. Por favor, inténtelo de nuevo.'
          )
        },
        /**
         * Callback que se ejecuta cuando la UI de autenticación está lista para mostrarse.
         */
        uiShown: () => {
          // La UI ya está visible para el usuario.
        }
      }
    }

    // Inicializa o reinicia FirebaseUI en el contenedor '#firebaseui-auth-container'
    if (!ui) {
      const newUi =
        firebaseui.auth.AuthUI.getInstance() ||
        new firebaseui.auth.AuthUI(auth)
      newUi.start('#firebaseui-auth-container', uiConfig)
      setUi(newUi)
    } else {
      ui.start('#firebaseui-auth-container', uiConfig)
    }

    /**
     * Cleanup: Desregistra el observador de autenticación cuando el componente se desmonta.
     */
    return () => unregisterAuthObserver()
  }, [auth, ui, navigate])

  return {
    /**
     * Instancia de FirebaseUI utilizada para renderizar la UI de autenticación.
     * Se inyecta automáticamente en `#firebaseui-auth-container`.
     * @type {firebaseui.auth.AuthUI | null}
     */
    ui,

    /**
     * Mensaje de error si la autenticación falla.
     * Es una cadena vacía si no hay errores.
     * @type {string}
     */
    errorText,

    /**
     * Indica si la autenticación sigue cargando.
     * `true` mientras se determina si hay un usuario autenticado o no.
     * `false` una vez que se ha verificado el estado del usuario.
     * @type {boolean}
     */
    loading,

    /**
     * Indica si el usuario está autenticado.
     * `true` si el usuario se encuentra autenticado.
     * `false` en caso contrario.
     * @type {boolean}
     */
    isAuthenticated
  }
}

export default useFirebaseAuth
